/* eslint-disable @typescript-eslint/no-unused-vars */
import { NavLink, useNavigate } from 'react-router-dom'
import { ChevronLeft, LogOut, Menu, ShoppingCart, User } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useStore } from '@/store/Store'
import { menuItems } from './SidebarItems'
import { Button } from '../ui/button'
import clsx from 'clsx'
import { getYear } from 'date-fns'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { ModeToggle } from './ModeToggle'
import { Badge } from '../ui/badge'
import { useMediaQuery } from '@/hooks/useMediaQuery'

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet'
import { TrialBanner } from '@/components/trial/TrialBanner'
import { TrialBadge } from '@/components/trial/TrialBadge'
import { useOwnerQuery } from '@/hooks/useOwnerSettings'
import { useEntitlementsQuery } from '@/hooks/useEntitlements'
import { AccountGateBanner } from '../AccountGateBanner'
import { useFindItemBySkuMutation } from '@/hooks/useItems'
import { toast } from 'sonner'
import type { CartItem } from '@/types/cart'
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner'
import PendingPlanBanner from '../billing/PendingPlanBanner'

const SIDEBAR_KEY = 'mogesapp_sidebar_collapsed'

const DefaultLayout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate()

  const { state, dispatch } = useStore()
  const role = state.userInfo?.user.role as
    | ('ADMIN' | 'OWNER' | 'STAFF')
    | undefined
  const { data: owner, status: ownerStatus } = useOwnerQuery(!!state.userInfo)
  const { data: ent } = useEntitlementsQuery(!!state.userInfo)
  const visible = menuItems.filter(
    (mi) => !mi.roles || (role && mi.roles.includes(role))
  )

  const { cart } = state
  const cartQty = useMemo(
    () => cart.items.reduce((a, c) => a + c.quantity, 0),
    [cart.items]
  )

  // Responsive
  const isMobile = useMediaQuery('(max-width: 1024px)')

  // Sidebar collapsée persistée
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem(SIDEBAR_KEY)
    if (saved === 'true' || saved === 'false') return saved === 'true'
    // par défaut : mobile -> fermé, desktop -> ouvert
    return false
  })

  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    // Si on passe en mobile, on ferme automatiquement
    if (isMobile) setCollapsed(true)
  }, [isMobile])

  const toggle = () => {
    setCollapsed((v) => {
      const next = !v
      localStorage.setItem(SIDEBAR_KEY, String(next))
      return next
    })
  }

  const logoutHandler = () => {
    dispatch({ type: 'USER_SIGNOUT' })
    navigate('/login')
  }

  const onMenuClick = () => {
    if (isMobile) setMobileOpen(true)
    else toggle()
  }

  const findItemBySku = useFindItemBySkuMutation()

  const addScannedItemToCart = useCallback(
    (item: {
      id: string
      name: string
      sku: string | null
      unitPrice: number
      stockOnHand: number
      type: 'PRODUCT' | 'SERVICE'
      imageUrl?: string | null
    }) => {
      const existing = cart.items.find((x) => x.itemId === item.id)
      const nextQty = existing ? existing.quantity + 1 : 1

      if (item.type !== 'SERVICE' && (item.stockOnHand ?? 0) < nextQty) {
        toast.warning('Stock insuffisant pour ajouter cet article au panier')
        return
      }

      const cartItem: CartItem = {
        id: item.id,
        itemId: item.id,
        name: item.name,
        sku: item.sku,
        unitPrice: item.unitPrice,
        quantity: nextQty,
      }

      dispatch({
        type: 'CART_ADD_ITEM',
        payload: cartItem,
      })

      toast.success(`${item.name} ajouté au panier`)
    },
    [cart.items, dispatch]
  )

  const handleBarcodeScan = useCallback(
    async (code: string) => {
      try {
        const item = await findItemBySku.mutateAsync(code)

        if (!item) {
          toast.error(`Aucun produit trouvé pour le code : ${code}`)
          return
        }

        addScannedItemToCart(item)
      } catch (error) {
        toast.error('Erreur pendant la recherche du produit')
      }
    },
    [findItemBySku, addScannedItemToCart]
  )

  useBarcodeScanner({
    enabled: !!state.userInfo,
    onScan: handleBarcodeScan,
    minLength: 3,
    resetDelayMs: 120,
  })

  return (
    <div className='flex h-screen'>
      {/* Lien "skip to content" pour accessibilité */}
      <a
        href='#main'
        className='sr-only focus:not-sr-only focus:absolute focus:m-2 focus:rounded focus:bg-primary/10 focus:px-3 focus:py-2'
      >
        Aller au contenu
      </a>

      {/* Sidebar */}
      <aside
        className={clsx(
          'border-r transition-[width,opacity] duration-200 overflow-hidden',
          collapsed ? 'w-0 opacity-0' : isMobile ? 'w-20' : 'w-64'
        )}
        aria-hidden={collapsed}
      >
        <div className='h-16 flex items-center justify-center'>
          <h2 className='text-2xl font-semibold'>
            {isMobile ? (
              <div className='flex flex-row items-center gap-3'>
                <img
                  src='/logo_mogesapp_light.png'
                  alt='MOGESAPP'
                  className='h-8 w-8 dark:hidden'
                  loading='eager'
                />
                {/* Mode sombre */}
                <img
                  src='/logo_mogesapp_dark.png'
                  alt='MOGESAPP'
                  className='h-8 w-8 hidden dark:block'
                  loading='eager'
                />
              </div>
            ) : (
              <>
                <div className='flex flex-row items-center gap-3'>
                  <img
                    src='/logo_mogesapp_light.png'
                    alt='MOGESAPP'
                    className='h-8 w-8 dark:hidden'
                    loading='eager'
                  />
                  {/* Mode sombre */}
                  <img
                    src='/logo_mogesapp_dark.png'
                    alt='MOGESAPP'
                    className='h-8 w-8 hidden dark:block'
                    loading='eager'
                  />
                  <h1 className='font-bold text-primary text-2xl'>MOGESAPP</h1>
                </div>
              </>
            )}
          </h2>
        </div>
        <ul className='mx-2'>
          {visible.map((mi) => (
            <li key={mi.key}>
              <NavLink
                to={mi.link}
                className={({ isActive }) =>
                  [
                    'flex items-center p-3 rounded-md hover:bg-primary/30 hover:text-primary transition-colors',
                    isActive ? 'bg-primary/30 text-primary' : '',
                  ].join(' ')
                }
                aria-label={mi.title}
              >
                <mi.Icon className='h-5 w-5' aria-hidden />
                {!collapsed && !isMobile && (
                  <span className='ml-4 text-sm font-normal'>{mi.title}</span>
                )}
              </NavLink>
            </li>
          ))}
          <li className='flex items-center p-3 mt-2 rounded-md hover:bg-red-100 dark:hover:bg-red-950/30'>
            <button
              className='flex items-center gap-4 w-full text-left'
              onClick={logoutHandler}
            >
              <LogOut className='text-red-900 dark:text-red-500' />
              {!isMobile && (
                <span className='text-sm font-medium text-red-900 dark:text-red-500'>
                  Déconnexion
                </span>
              )}
            </button>
          </li>
        </ul>
      </aside>

      {/* Main column */}
      <div className='w-full'>
        <header className='flex h-14 lg:h-[60px] items-center justify-between gap-4 border-b px-6'>
          <div className='flex items-center'>
            {/* En mobile: bouton ouvre le Sheet. En desktop: toggle sidebar */}
            {isMobile ? (
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    onClick={onMenuClick}
                    className='rounded-full hover:text-primary'
                    size='icon'
                    variant='ghost'
                    aria-label='Ouvrir le menu'
                    aria-controls='mobile-drawer'
                    aria-expanded={mobileOpen}
                  >
                    <Menu className='h-6 w-6' />
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side='left'
                  className='p-0 w-[50vw] max-w-xs'
                  id='mobile-drawer'
                >
                  <SheetHeader className='px-4 py-3 border-b'>
                    <SheetTitle>
                      <div className='flex flex-row items-center gap-3'>
                        <img
                          src='/logo_mogesapp_light.png'
                          alt='MOGESAPP'
                          className='h-5 w-5 dark:hidden'
                          loading='eager'
                        />
                        {/* Mode sombre */}
                        <img
                          src='/logo_mogesapp_dark.png'
                          alt='MOGESAPP'
                          className='h-5 w-5 hidden dark:block'
                          loading='eager'
                        />
                        <h1 className='font-bold text-primary text-md'>
                          MOGESAPP
                        </h1>
                      </div>
                    </SheetTitle>
                  </SheetHeader>
                  <nav className='p-2'>
                    <ul>
                      {visible.map((mi) => (
                        <li key={mi.key}>
                          <SheetClose asChild>
                            <NavLink
                              to={mi.link}
                              className={({ isActive }) =>
                                [
                                  'flex items-center rounded-md hover:bg-primary/30 hover:text-primary transition-colors',
                                  isActive ? 'bg-primary/30 text-primary' : '',
                                ].join(' ')
                              }
                              aria-label={mi.title}
                            >
                              <div className='flex p-3'>
                                <mi.Icon className='h-5 w-5' aria-hidden />
                                <span className='ml-4 text-sm font-normal'>
                                  {mi.title}
                                </span>
                              </div>
                            </NavLink>
                          </SheetClose>
                        </li>
                      ))}
                    </ul>
                    <div className='p-2 mt-2'>
                      <SheetClose asChild>
                        <button
                          onClick={logoutHandler}
                          className='w-full flex items-center gap-3 p-3 rounded-md hover:bg-red-100 dark:hover:bg-red-950/30'
                        >
                          <LogOut className='text-red-900 dark:text-red-500 h-5 w-5' />
                          <span className='text-sm font-medium text-red-900 dark:text-red-500'>
                            Déconnexion
                          </span>
                        </button>
                      </SheetClose>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            ) : (
              <Button
                onClick={onMenuClick}
                className='rounded-full hover:text-primary'
                size='icon'
                variant='ghost'
                aria-label='Basculer le menu latéral'
                aria-expanded={!collapsed}
                aria-controls='sidebar'
              >
                {collapsed ? (
                  <Menu className='h-6 w-6' />
                ) : (
                  <ChevronLeft className='h-6 w-6' />
                )}
              </Button>
            )}
          </div>

          <div className='flex items-center gap-4 md:gap-2 lg:gap-4'>
            {ownerStatus === 'success' && owner ? (
              <TrialBadge owner={owner} />
            ) : null}
            <div className='relative'>
              <Button
                onClick={() => navigate('/dashboard')}
                className='rounded-full'
                size='icon'
                variant='ghost'
                aria-label='Ouvrir le panier'
              >
                <ShoppingCart className='h-6 w-6' />
              </Button>
              {cartQty > 0 && (
                <Badge
                  className='absolute -top-1 -right-1'
                  variant='destructive'
                >
                  {cartQty}
                </Badge>
              )}
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className='rounded-full'
                  size='icon'
                  variant='ghost'
                  aria-label='Ouvrir le menu utilisateur'
                >
                  <User className='h-6 w-6' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuLabel>Mon compte</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/me')}>
                  Profil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logoutHandler}>
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <ModeToggle />
          </div>
        </header>

        <AccountGateBanner
          ent={ent}
          onRenew={() => navigate('/subscriptions')}
          onLogout={logoutHandler}
        />
        <div className='px-4 md:px-6 mt-4'>
          <PendingPlanBanner />
        </div>

        <main
          id='main'
          className='flex-1 min-h-[280px] h-[86vh] p-4 md:p-6 backdrop-blur-[35px] dark:bg-muted/40 bg-muted/60 dark:shadow-2xl dark:shadow-black mx-auto pt-10 z-2 overflow-y-auto'
        >
          {ownerStatus === 'success' && owner ? (
            <TrialBanner owner={owner} />
          ) : null}

          {children}
        </main>

        <footer className='flex md:flex-row md:mt-2 flex-col items-center mx-5 justify-between pt-5'>
          <div className='text-sm'>
            MOGESAPP ©{getYear(new Date())} Tous droits réservés
          </div>
          <div className='font-extralight text-sm'>
            Créé par{' '}
            <a
              href='http://konemory.com'
              target='_blank'
              rel='noopener noreferrer'
              className='hover:font-medium hover:text-primary'
            >
              Koné Mory
            </a>{' '}
            <span>+225 07 09 14 97 47</span>
          </div>
        </footer>
      </div>
    </div>
  )
}

export default DefaultLayout
